import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
} from '@roger/ui-components';
import { Gauge, Droplets, Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ClickableTextWithModals from './ClickableTextWithModals';
import type {
  HomeHealthOrderItem,
  FaceToFaceVisitItem,
  PastVisitDiagnosis,
  AuthorizedDiagnosisItem,
} from '@/types/medicalData.types';
import { Medication } from '@/types/medication.types';
import { Wound } from '@/types/wound.types';

interface MedicalDataModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  dataType: string;
  data: unknown;
  onOpenNestedModal?: (dataType: string) => void;
  pdfPaths?: string[];
  onOpenPdf?: (pdfPath: string) => void;
}

const MedicalDataModal: React.FC<MedicalDataModalProps> = ({
  open,
  onOpenChange,
  title,
  dataType,
  data,
  onOpenNestedModal,
  pdfPaths = [],
  onOpenPdf,
}) => {
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Helper to find full PDF path from filename
  const findPdfPath = (filename: string): string | null => {
    if (!pdfPaths || pdfPaths.length === 0) return null;
    return pdfPaths.find(path => path.endsWith(filename)) || null;
  };

  // Reusable function to render source documents with clickable PDFs
  const renderSourceDocuments = (source: Record<string, unknown>) => {
    return (
      <div className='space-y-2'>
        {Object.entries(source).map(([filename, pages]) => {
          const pdfPath = findPdfPath(filename);
          const isClickable = pdfPath && onOpenPdf;

          return (
            <div key={filename} className='flex flex-col gap-1'>
              {isClickable ? (
                <button
                  onClick={() => onOpenPdf(pdfPath)}
                  className='font-medium text-blue-600 hover:text-blue-800 hover:underline text-left'
                >
                  {filename}
                </button>
              ) : (
                <span className='font-medium text-gray-900'>{filename}</span>
              )}
              <span className='text-xs text-gray-600'>
                Pages: {Array.isArray(pages) ? pages.join(', ') : String(pages)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMedications = (medications: Medication[]) => {
    if (!Array.isArray(medications) || medications.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No medications recorded</p>
          <p className='text-sm'>No active medications found for this visit</p>
        </div>
      );
    }

    const colors = ['border-blue-500', 'border-purple-500'];

    return (
      <div className='space-y-3 max-h-[80vh] overflow-y-auto'>
        {medications.map((med, index: number) => {
          const medicationName = med.Medication || 'Unknown Medication';
          const color = colors[index % 2];

          const details: string[] = [];

          if (med.Dose1) {
            const dose = `${med.Dose1}${med.DoseType ? ' ' + med.DoseType : ''}`;
            details.push(`Dose: ${dose}`);
          }
          if (med.MedicationInstructions) {
            details.push(`Instructions: ${med.MedicationInstructions}`);
          }
          if (med.MedicationFrequency) {
            details.push(`Frequency: ${med.MedicationFrequency}`);
          }
          if (med.Reason) {
            details.push(`Reason: ${med.Reason}`);
          }
          if (med.MedicationSpecialInstructions) {
            details.push(`Special Notes: ${med.MedicationSpecialInstructions}`);
          }

          if (med.newStartDate) {
            details.push(`Started: ${med.newStartDate}`);
          }
          if (med.newDiscontinueDate) {
            details.push(`Discontinue: ${med.newDiscontinueDate}`);
          }

          return (
            <div
              key={index}
              className={`bg-white border-l-4 ${color} border rounded-lg overflow-hidden`}
            >
              <div className='bg-gray-50 border-b px-4 py-3 flex items-center justify-between'>
                <h4 className='font-semibold text-gray-900'>
                  {medicationName}
                </h4>
              </div>
              <div className='p-4 text-sm text-gray-700 whitespace-pre-wrap'>
                {details.join('\n')}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWounds = (wounds: Wound[]) => {
    if (!Array.isArray(wounds) || wounds.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No wounds recorded</p>
          <p className='text-sm'>No wounds found for this visit</p>
        </div>
      );
    }

    const getWoundStatus = (w: Wound) => {
      if (w.status_closed)
        return {
          label: 'CLOSED',
          color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        };
      if (w.status_open)
        return {
          label: 'OPEN',
          color: 'bg-amber-100 text-amber-800 border-amber-200',
        };
      return {
        label: 'HEALING',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
      };
    };

    const getWoundTitle = (w: Wound, idx: number) => {
      const parts: string[] = [];
      if (w.location) parts.push(w.location);
      if (w.wound_type) parts.push(w.wound_type);
      return parts.length > 0 ? parts.join(' - ') : `Wound ${idx + 1}`;
    };

    const getWoundStage = (w: Wound): string | null => {
      if (w.stage_1) return 'Stage 1';
      if (w.stage_2) return 'Stage 2';
      if (w.stage_3) return 'Stage 3';
      if (w.stage_4) return 'Stage 4';
      if (w.stage_unstageable) return 'Unstageable';
      if (w.stage_susp_dti) return 'Suspected DTI';
      return null;
    };

    const colors = [
      'border-blue-500',
      'border-purple-500',
      'border-emerald-500',
      'border-amber-500',
    ];

    return (
      <div className='space-y-4 max-h-[80vh] overflow-y-auto pr-4'>
        {wounds.map((wound, index: number) => {
          const status = getWoundStatus(wound);
          const woundTitle = getWoundTitle(wound, index);
          const stage = getWoundStage(wound);
          const color = colors[index % colors.length];

          return (
            <div
              key={index}
              className={`bg-white border-l-4 ${color} border rounded-lg overflow-hidden`}
            >
              <div className='bg-gray-50 border-b px-4 py-3'>
                <div className='flex items-center justify-between flex-wrap gap-2'>
                  <div>
                    <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                      {woundTitle}
                    </h4>
                    {wound.onset_date && (
                      <p className='text-xs text-gray-600 mt-1'>
                        Onset: {wound.onset_date}
                      </p>
                    )}
                  </div>
                  <div className='flex items-center gap-2'>
                    {stage && (
                      <Badge className='bg-purple-100 text-purple-800 text-xs'>
                        {stage}
                      </Badge>
                    )}
                    <Badge
                      className={`text-xs ${
                        status.label === 'CLOSED'
                          ? 'bg-green-100 text-green-800'
                          : status.label === 'OPEN'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {status.label}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Wound Details */}
              <div className='p-4 space-y-4'>
                {/* Clinical Classification */}
                {(wound.wound_type ||
                  stage ||
                  wound.present_on_admission !== undefined) && (
                  <div>
                    <div className='grid grid-cols-3 gap-4'>
                      {wound.wound_type && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>
                            Wound Type
                          </p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.wound_type}
                          </p>
                        </div>
                      )}
                      {stage && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>Stage</p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {stage}
                          </p>
                        </div>
                      )}
                      {wound.present_on_admission !== undefined && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>
                            On Admission
                          </p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.present_on_admission ? 'Yes' : 'No'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Measurements */}
                {(wound.size_length ||
                  wound.size_width ||
                  wound.size_depth) && (
                  <>
                    <div className='flex items-center gap-2 mb-3'>
                      <Gauge className='w-4 h-4 text-slate-600' />
                      <p className='uppercase text-xs font-semibold text-slate-500 tracking-wide'>
                        Measurements
                      </p>
                    </div>
                    <div className='grid grid-cols-3 gap-4 '>
                      {wound.size_length && (
                        <div>
                          <p className='text-xs text-slate-600 mb-1'>Length</p>
                          <p className='text-2xl font-bold text-slate-900'>
                            {wound.size_length}
                          </p>
                          <p className='text-xs text-slate-500'>cm</p>
                        </div>
                      )}
                      {wound.size_width && (
                        <div>
                          <p className='text-xs text-slate-600 mb-1'>Width</p>
                          <p className='text-2xl font-bold text-slate-900'>
                            {wound.size_width}
                          </p>
                          <p className='text-xs text-slate-500'>cm</p>
                        </div>
                      )}
                      {wound.size_depth && (
                        <div>
                          <p className='text-xs text-slate-600 mb-1'>Depth</p>
                          <p className='text-2xl font-bold text-slate-900'>
                            {wound.size_depth}
                          </p>
                          <p className='text-xs text-slate-500'>cm</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Undermining */}
                {wound.undermining_length &&
                  wound.undermining_length !== '0' && (
                    <div className='bg-orange-50 rounded-lg p-4 border border-orange-200'>
                      <h3 className='text-sm font-semibold text-orange-900 mb-3'>
                        Undermining
                      </h3>
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <p className='text-xs text-orange-700 mb-1'>Length</p>
                          <p className='text-lg font-bold text-orange-900'>
                            {wound.undermining_length} cm
                          </p>
                        </div>
                        {wound.undermining_direction && (
                          <div>
                            <p className='text-xs text-orange-700 mb-1'>
                              Direction
                            </p>
                            <p className='text-lg font-bold text-orange-900'>
                              {wound.undermining_direction}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Drainage */}
                {(wound.drainage_type || wound.drainage_amount) && (
                  <>
                    <div className='flex items-center gap-2 mb-3'>
                      <Droplets className='w-4 h-4 text-slate-600' />
                      <p className='uppercase text-xs font-semibold text-slate-500 tracking-wide'>
                        Drainage
                      </p>
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                      {wound.drainage_type && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>Type</p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.drainage_type}
                          </p>
                        </div>
                      )}
                      {wound.drainage_amount && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>Amount</p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.drainage_amount}
                          </p>
                        </div>
                      )}
                      {wound.drainage_other && (
                        <div className='col-span-2 bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>Notes</p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.drainage_other}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Odor & Pressure */}
                {(wound.odor_type || wound.pressure_option_other) && (
                  <>
                    <p className='uppercase text-xs font-semibold text-slate-500 tracking-wide'>
                      Additional Characteristics
                    </p>
                    <div className='grid grid-cols-2 gap-4'>
                      {wound.odor_type && wound.odor_type !== 'None' && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>Odor</p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.odor_type}
                          </p>
                          {wound.odor_other && (
                            <p className='text-xs text-slate-600 mt-1'>
                              {wound.odor_other}
                            </p>
                          )}
                        </div>
                      )}
                      {wound.pressure_option_other && (
                        <div className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                          <p className='text-xs text-slate-600 mb-1'>
                            Pressure Type
                          </p>
                          <p className='text-sm font-semibold text-slate-900'>
                            {wound.pressure_option_other}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Treatment & Response */}
                {(wound.treatment || wound.patient_response) && (
                  <div className='bg-blue-50 rounded-lg p-4 border border-blue-200'>
                    <div className='flex items-center gap-2 mb-3'>
                      <Info className='w-4 h-4 text-blue-700' />
                      <h3 className='text-sm font-semibold text-blue-900'>
                        Treatment & Response
                      </h3>
                    </div>
                    <div className='space-y-3'>
                      {wound.treatment && (
                        <div>
                          <p className='text-xs text-blue-700 mb-1'>
                            Treatment
                          </p>
                          <p className='text-sm text-blue-900 whitespace-pre-wrap'>
                            {wound.treatment}
                          </p>
                        </div>
                      )}
                      {wound.patient_response && (
                        <div>
                          <p className='text-xs text-blue-700 mb-1'>
                            Patient Response
                          </p>
                          <p className='text-sm text-blue-900 whitespace-pre-wrap'>
                            {wound.patient_response}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Information */}
                {wound.additional_information && (
                  <div className='bg-slate-50 rounded-lg p-4 border border-slate-200'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Info className='w-4 h-4 text-slate-600' />
                      <h3 className='text-sm font-semibold text-slate-700'>
                        Additional Information
                      </h3>
                    </div>
                    <p className='text-sm text-slate-900 whitespace-pre-wrap'>
                      {wound.additional_information}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderHomeHealthOrder = (homeHealthOrder: HomeHealthOrderItem[]) => {
    // Check if it's an array (new schema)
    if (Array.isArray(homeHealthOrder)) {
      if (homeHealthOrder.length === 0) {
        return (
          <div className='text-center py-8 text-gray-500'>
            <p className='font-medium'>No home health orders available</p>
            <p className='text-sm'>Order information not found</p>
          </div>
        );
      }

      return (
        <div className='space-y-6 max-h-[80vh] overflow-y-auto pr-2'>
          {(homeHealthOrder as HomeHealthOrderItem[]).map(
            (order, orderIndex) => {
              const orderText = order.home_health_order;
              const source = order.source;
              const reasoning = order.reasoning;

              return (
                <div
                  key={orderIndex}
                  className='border-2 border-gray-200 rounded-lg p-4 space-y-3'
                >
                  {homeHealthOrder.length > 1 && (
                    <div className='mb-3'>
                      <Badge className='bg-blue-100 text-blue-800 border-blue-200'>
                        Order {orderIndex + 1} of {homeHealthOrder.length}
                      </Badge>
                    </div>
                  )}

                  {/* Home Health Order Section */}
                  {orderText && typeof orderText === 'string' && (
                    <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
                      <div className='border-b px-4 py-3 flex items-center gap-2'>
                        <h4 className='font-semibold text-blue-700 text-[0.95rem]'>
                          Home Health Order
                        </h4>
                      </div>
                      <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <p className='whitespace-pre-wrap'>{children}</p>
                            ),
                            li: ({ children }) => (
                              <li className='whitespace-pre-wrap'>
                                {children}
                              </li>
                            ),
                          }}
                        >
                          {orderText}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Source Section */}
                  {source && typeof source === 'object' && source !== null && (
                    <div className='bg-white border-l-4 border-purple-500 border rounded-lg overflow-hidden'>
                      <div className='border-b px-4 py-3 flex items-center gap-2'>
                        <h4 className='font-semibold text-purple-700 text-[0.95rem]'>
                          Source Documents
                        </h4>
                      </div>
                      <div className='p-4 text-gray-700 text-sm leading-relaxed'>
                        {renderSourceDocuments(source)}
                      </div>
                    </div>
                  )}

                  {/* Reasoning Section */}
                  {reasoning && typeof reasoning === 'string' && (
                    <div className='bg-white border-l-4 border-green-500 border rounded-lg overflow-hidden'>
                      <div className='border-b px-4 py-3 flex items-center gap-2'>
                        <h4 className='font-semibold text-green-700 text-[0.95rem]'>
                          Reasoning
                        </h4>
                      </div>
                      <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <p className='whitespace-pre-wrap'>{children}</p>
                            ),
                            li: ({ children }) => (
                              <li className='whitespace-pre-wrap'>
                                {children}
                              </li>
                            ),
                          }}
                        >
                          {reasoning}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      );
    }

    // Fallback for old object-based schema
    if (!homeHealthOrder || typeof homeHealthOrder !== 'object') {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No home health order available</p>
          <p className='text-sm'>Order information not found</p>
        </div>
      );
    }

    const orderData = homeHealthOrder as Record<string, unknown>;

    // Fallback to old schema handling
    const orderEntries = Object.entries(orderData).filter(
      ([, value]) => value && value !== '' && value !== false
    );

    if (orderEntries.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No home health order available</p>
          <p className='text-sm'>Order information not found</p>
        </div>
      );
    }

    return (
      <div className='space-y-3 max-h-[80vh] overflow-y-auto pr-2'>
        {orderEntries.map(([key, value]) => {
          // Determine icon and color based on content type
          let borderColor = 'border-blue-500';
          let bgColor = 'bg-blue-50';
          let textColor = 'text-blue-700';

          if (key.toLowerCase().includes('diagnosis')) {
            borderColor = 'border-red-500';
            bgColor = 'bg-red-50';
            textColor = 'text-red-700';
          } else if (
            key.toLowerCase().includes('visit') ||
            key.toLowerCase().includes('face')
          ) {
            borderColor = 'border-green-500';
            bgColor = 'bg-green-50';
            textColor = 'text-green-700';
          } else if (
            key.toLowerCase().includes('order') ||
            key.toLowerCase().includes('info')
          ) {
            borderColor = 'border-amber-500';
            bgColor = 'bg-amber-50';
            textColor = 'text-amber-700';
          }

          // Format the title nicely
          const title = key
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          return (
            <div
              key={key}
              className={`bg-white border-l-4 ${borderColor} border rounded-lg overflow-hidden`}
            >
              <div
                className={`${bgColor} border-b px-4 py-3 flex items-center gap-2`}
              >
                <h4 className={`font-semibold ${textColor} text-[0.95rem]`}>
                  {title}
                </h4>
              </div>
              <div className='p-4 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap'>
                {formatValue(value)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFaceToFaceVisits = (faceToFaceVisits: FaceToFaceVisitItem[]) => {
    // Check if it's an array
    if (!Array.isArray(faceToFaceVisits)) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No face-to-face visits available</p>
          <p className='text-sm'>Visit information not found</p>
        </div>
      );
    }

    if (faceToFaceVisits.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No face-to-face visits available</p>
          <p className='text-sm'>Visit information not found</p>
        </div>
      );
    }

    return (
      <div className='space-y-6 max-h-[80vh] overflow-y-auto pr-2'>
        {faceToFaceVisits.map((visit, visitIndex) => {
          const summary = visit.face_to_face_visit_summary;
          const source = visit.source;
          const reasoning = visit.reasoning;

          return (
            <div
              key={visitIndex}
              className='border-2 border-gray-200 rounded-lg p-4 space-y-3'
            >
              {faceToFaceVisits.length > 1 && (
                <div className='mb-3'>
                  <Badge className='bg-blue-100 text-blue-800 border-blue-200'>
                    Visit {visitIndex + 1} of {faceToFaceVisits.length}
                  </Badge>
                </div>
              )}

              {/* Face-to-Face Visit Summary Section */}
              {summary && typeof summary === 'string' && (
                <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
                  <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                    <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                      Face-to-Face Visit Summary
                    </h4>
                  </div>
                  <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p className='whitespace-pre-wrap'>{children}</p>
                        ),
                        li: ({ children }) => (
                          <li className='whitespace-pre-wrap'>{children}</li>
                        ),
                      }}
                    >
                      {summary}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Source Section */}
              {source && typeof source === 'object' && source !== null && (
                <div className='bg-white border-l-4 border-purple-500 border rounded-lg overflow-hidden'>
                  <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                    <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                      Source Documents
                    </h4>
                  </div>
                  <div className='p-4 text-gray-700 text-sm leading-relaxed'>
                    {renderSourceDocuments(source)}
                  </div>
                </div>
              )}

              {/* Reasoning Section */}
              {reasoning && typeof reasoning === 'string' && (
                <div className='bg-white border-l-4 border-green-500 border rounded-lg overflow-hidden'>
                  <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                    <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                      Reasoning
                    </h4>
                  </div>
                  <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p className='whitespace-pre-wrap'>{children}</p>
                        ),
                        li: ({ children }) => (
                          <li className='whitespace-pre-wrap'>{children}</li>
                        ),
                      }}
                    >
                      {reasoning}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTranscript = (transcript?: Record<string, string>) => {
    if (!transcript) return <></>;

    const allTranscripts = Object.values(transcript).reduce((acc, curr) => {
      return acc + '\n' + curr;
    }, '');

    return (
      <div className='max-h-[80vh] overflow-y-auto'>
        <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
          <div className='p-4 text-sm text-gray-700 whitespace-pre-wrap'>
            <ClickableTextWithModals
              text={allTranscripts}
              onOpenModal={onOpenNestedModal}
              size='sm'
            />
          </div>
        </div>
      </div>
    );
  };

  const renderKeyValuePairs = (obj: Record<string, unknown> | unknown) => {
    if (!obj || typeof obj !== 'object') {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No data available</p>
        </div>
      );
    }

    const entries = Object.entries(obj as Record<string, unknown>).filter(
      ([, value]) => value && value !== '' && value !== false
    );

    if (entries.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No data available</p>
        </div>
      );
    }

    const colors = ['border-blue-500', 'border-purple-500'];

    return (
      <div className='space-y-3 max-h-[80vh] overflow-y-auto'>
        {entries.map(([key, value], index) => {
          const color = colors[index % 2];
          const formattedKey = key
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          const isString = typeof value === 'string';
          const hasAtReferences = isString && value.includes('@');

          return (
            <div
              key={key}
              className={`bg-white border-l-4 ${color} border rounded-lg overflow-hidden`}
            >
              <div className='bg-gray-50 border-b px-4 py-3'>
                <h4 className='font-semibold text-gray-900'>{formattedKey}</h4>
              </div>
              <div className='p-4 text-sm text-gray-700'>
                {hasAtReferences && onOpenNestedModal ? (
                  <ClickableTextWithModals
                    text={value as string}
                    onOpenModal={onOpenNestedModal}
                    size='sm'
                  />
                ) : isString ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => (
                        <p className='whitespace-pre-wrap'>{children}</p>
                      ),
                      li: ({ children }) => (
                        <li className='whitespace-pre-wrap'>{children}</li>
                      ),
                    }}
                  >
                    {value}
                  </ReactMarkdown>
                ) : (
                  <div className='whitespace-pre-wrap'>
                    {formatValue(value)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAuthorizedDiagnoses = (
    diagnoses: AuthorizedDiagnosisItem[] | unknown
  ) => {
    // Check if it's an array
    if (!Array.isArray(diagnoses)) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No authorized diagnoses available</p>
          <p className='text-sm'>Diagnosis information not found</p>
        </div>
      );
    }

    if (diagnoses.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No authorized diagnoses available</p>
          <p className='text-sm'>Diagnosis information not found</p>
        </div>
      );
    }

    return (
      <div className='space-y-6 max-h-[80vh] overflow-y-auto pr-2'>
        {(diagnoses as AuthorizedDiagnosisItem[]).map(
          (diagnosis, diagnosisIndex) => {
            const diagnosisText = diagnosis.authorized_diagnoses;
            const source = diagnosis.source;
            const reasoning = diagnosis.reasoning;

            return (
              <div
                key={diagnosisIndex}
                className='border-2 border-gray-200 rounded-lg p-4 space-y-3'
              >
                {diagnoses.length > 1 && (
                  <div className='mb-3'>
                    <Badge className='bg-blue-100 text-blue-800 border-blue-200'>
                      Diagnosis {diagnosisIndex + 1} of {diagnoses.length}
                    </Badge>
                  </div>
                )}

                {/* Authorized Diagnosis Section */}
                {diagnosisText && typeof diagnosisText === 'string' && (
                  <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
                    <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                      <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                        Authorized Diagnosis
                      </h4>
                    </div>
                    <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className='whitespace-pre-wrap'>{children}</p>
                          ),
                          li: ({ children }) => (
                            <li className='whitespace-pre-wrap'>{children}</li>
                          ),
                        }}
                      >
                        {diagnosisText}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Source Section */}
                {source && typeof source === 'object' && source !== null && (
                  <div className='bg-white border-l-4 border-purple-500 border rounded-lg overflow-hidden'>
                    <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                      <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                        Source Documents
                      </h4>
                    </div>
                    <div className='p-4 text-gray-700 text-sm leading-relaxed'>
                      {renderSourceDocuments(source)}
                    </div>
                  </div>
                )}

                {/* Reasoning Section */}
                {reasoning && typeof reasoning === 'string' && (
                  <div className='bg-white border-l-4 border-green-500 border rounded-lg overflow-hidden'>
                    <div className='bg-gray-50 border-b px-4 py-3 flex items-center gap-2'>
                      <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                        Reasoning
                      </h4>
                    </div>
                    <div className='p-4 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none'>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className='whitespace-pre-wrap'>{children}</p>
                          ),
                          li: ({ children }) => (
                            <li className='whitespace-pre-wrap'>{children}</li>
                          ),
                        }}
                      >
                        {reasoning}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
    );
  };

  const renderPastVisitDiagnoses = (visits: PastVisitDiagnosis[]) => {
    if (!Array.isArray(visits) || visits.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No past visit diagnoses recorded</p>
          <p className='text-sm'>
            No historical diagnosis information available
          </p>
        </div>
      );
    }

    const colors = ['border-blue-500', 'border-purple-500'];

    // Format ICD-10 code with dot after 3rd character
    const formatIcdCode = (code: string): string => {
      if (!code) return '';
      // Remove any existing dots first
      const cleanCode = code.replace(/\./g, '');
      // Add dot after 3rd character if code is longer than 3 characters
      if (cleanCode.length > 3) {
        return `${cleanCode.slice(0, 3)}.${cleanCode.slice(3)}`;
      }
      return cleanCode;
    };

    return (
      <div className='space-y-4 max-h-[80vh] overflow-y-auto pr-2'>
        {visits.map((visit, index: number) => {
          const color = colors[index % 2];
          const visitTitle = visit.formType || 'Visit';
          const visitDate = visit.effectiveDate
            ? new Date(visit.effectiveDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : 'Unknown Date';

          const diagnoses = visit.diagnoses;
          const diagnosesCount = diagnoses?.length || 0;

          return (
            <div
              key={index}
              className='border-2 border-gray-200 rounded-lg p-4 space-y-3'
            >
              {/* Visit Header */}
              <div
                className={`bg-white border-l-4 ${color} border rounded-lg overflow-hidden`}
              >
                <div className='bg-gray-50 border-b px-4 py-3'>
                  <div className='flex items-center justify-between flex-wrap gap-2'>
                    <div>
                      <h4 className='font-semibold text-gray-900 text-[0.95rem]'>
                        {visitTitle}
                      </h4>
                      <p className='text-xs text-gray-600 mt-1'>{visitDate}</p>
                    </div>
                    <div className='flex items-center gap-2'>
                      {visit.effectiveDateStatus && (
                        <Badge className='bg-green-100 text-green-800 text-xs'>
                          {visit.effectiveDateStatus.toUpperCase()}
                        </Badge>
                      )}
                      <Badge className='bg-gray-100 text-gray-800 text-xs'>
                        {diagnosesCount} Diagnoses
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Diagnoses List */}
                <div className='p-4'>
                  {diagnoses && diagnoses.length > 0 ? (
                    <div className='space-y-2'>
                      {diagnoses.map((diagnosis, idx) => {
                        const isPrimary = diagnosis.isPrimary;
                        const formattedCode = formatIcdCode(diagnosis.code);

                        return (
                          <div
                            key={diagnosis.id || idx}
                            className='flex items-start gap-2 py-2 border-b border-gray-100 last:border-0'
                          >
                            {/* Code Badge */}
                            <span className='px-2 py-1 bg-primary-100 text-primary-700 font-mono text-xs rounded font-semibold shrink-0'>
                              {formattedCode}
                            </span>

                            {/* Primary Badge */}
                            {isPrimary && (
                              <Badge className='bg-blue-600 text-white text-xs font-semibold shrink-0'>
                                PRIMARY
                              </Badge>
                            )}

                            {/* Full Diagnosis Text */}
                            <span className='text-sm text-gray-900 flex-1'>
                              {diagnosis.display}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className='text-gray-500 text-sm'>
                      No diagnoses recorded for this visit
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderHnpSummary = (content: string) => {
    if (!content) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No HnP summary available</p>
        </div>
      );
    }

    // Parse XML-like tags from the content
    const tagRegex = /<([^>]+)>\s*([\s\S]*?)\s*<\/\1>/g;
    const sections: Array<{ tag: string; content: string }> = [];
    let match;

    while ((match = tagRegex.exec(content)) !== null) {
      sections.push({
        tag: match[1],
        content: match[2].trim(),
      });
    }

    if (sections.length === 0) {
      // Fallback if no tags found - show as simple content
      return (
        <div className='max-h-[80vh] overflow-y-auto'>
          <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
            <div className='bg-gray-50 border-b px-4 py-3'>
              <h4 className='font-semibold text-gray-900'>Content</h4>
            </div>
            <div className='p-4 text-sm text-gray-700 whitespace-pre-wrap'>
              {content}
            </div>
          </div>
        </div>
      );
    }

    // Color palette for different sections
    const colors = [
      'border-blue-500',
      'border-purple-500',
      'border-green-500',
      'border-orange-500',
      'border-pink-500',
      'border-indigo-500',
      'border-teal-500',
      'border-red-500',
    ];

    const textColors = [
      'text-blue-700',
      'text-purple-700',
      'text-green-700',
      'text-orange-700',
      'text-pink-700',
      'text-indigo-700',
      'text-teal-700',
      'text-red-700',
    ];

    // Format tag name for display
    const formatTagName = (tag: string) => {
      return tag
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <div className='space-y-4 max-h-[80vh] overflow-y-auto pr-2'>
        {sections.map((section, index) => {
          const colorIndex = index % colors.length;
          const borderColor = colors[colorIndex];
          const textColor = textColors[colorIndex];

          return (
            <div
              key={index}
              className={`bg-white border-l-4 ${borderColor} border rounded-lg overflow-hidden`}
            >
              <div className={`border-b px-4 py-3`}>
                <h4 className={`font-semibold ${textColor} text-base`}>
                  {formatTagName(section.tag)}
                </h4>
              </div>
              <div className='p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed'>
                {section.content}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSimpleContent = (content: string) => {
    if (!content) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No data available</p>
        </div>
      );
    }

    return (
      <div className='max-h-[80vh] overflow-y-auto'>
        <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
          <div className='bg-gray-50 border-b px-4 py-3'>
            <h4 className='font-semibold text-gray-900'>Content</h4>
          </div>
          <div className='p-4 text-sm text-gray-700 whitespace-pre-wrap'>
            {formatValue(content)}
          </div>
        </div>
      </div>
    );
  };

  const renderDemographics = (obj: Record<string, unknown> | unknown) => {
    if (!obj || typeof obj !== 'object') {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No data available</p>
        </div>
      );
    }

    const entries = Object.entries(obj as Record<string, unknown>).filter(
      ([, value]) => value && value !== '' && value !== false
    );

    if (entries.length === 0) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p className='font-medium'>No data available</p>
        </div>
      );
    }

    // Format key names
    const formatKey = (key: string) => {
      return key
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <div className='max-h-[80vh] overflow-y-auto'>
        <div className='bg-white border-l-4 border-blue-500 border rounded-lg overflow-hidden'>
          <div className='p-4'>
            <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
              {entries.map(([key, value]) => {
                const isString = typeof value === 'string';
                const hasAtReferences = isString && value.includes('@');

                return (
                  <div
                    key={key}
                    className='border-b border-gray-100 pb-3 last:border-0'
                  >
                    <dt className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1'>
                      {formatKey(key)}
                    </dt>
                    <dd className='text-sm font-medium text-gray-900'>
                      {hasAtReferences && onOpenNestedModal ? (
                        <ClickableTextWithModals
                          text={value as string}
                          onOpenModal={onOpenNestedModal}
                          size='sm'
                        />
                      ) : (
                        formatValue(value)
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (dataType) {
      case 'medications':
        return renderMedications(data as Medication[]);
      case 'wounds':
        return renderWounds(data as Wound[]);
      case 'authorized_diagnoses':
        return renderAuthorizedDiagnoses(data as AuthorizedDiagnosisItem[]);
      case 'past_visit_diagnoses':
        return renderPastVisitDiagnoses(data as PastVisitDiagnosis[]);
      case 'hnp_summary':
        return renderHnpSummary(data as string);
      case 'narrative':
        return renderSimpleContent(data as string);
      case 'visit_transcript':
        return renderTranscript(data as Record<string, string>);
      case 'home_health_orders':
        return renderHomeHealthOrder(data as HomeHealthOrderItem[]);
      case 'face_to_face_visits':
        return renderFaceToFaceVisits(data as FaceToFaceVisitItem[]);
      case 'demographics':
        return renderDemographics(data as Record<string, unknown>);
      case 'medical_form':
        return renderKeyValuePairs(data as Record<string, unknown>);
      default:
        return (
          <pre className='text-xs bg-gray-50 p-4 rounded border overflow-auto max-h-[80vh]'>
            {JSON.stringify(data, null, 2)}
          </pre>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className='flex-1 overflow-hidden'>{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
};

export default MedicalDataModal;
